/* eslint-disable max-len */
import React from 'react';
import { NotAvailable } from './partials/NotAvailable';
import { FULFILLMENT_METHODS } from '../constants';
import { ApplianceShipping } from './partials/shipping/ApplianceShipping';
import { ApplianceShippingDetails } from './partials/shipping/ApplianceShipping_Details';
import {
  isApplianceDeliveryBackordered,
  isApplianceDeliveryOutOfStock,
  isApplianceDeliveryUnavailable,
  isApplianceDeliveryError,
  formatDate,
  showTodayOrTomorrow
} from '../helper/utils';
import { getApplianceDeliveryDate } from '../helper/christmasMessagingUtils';
import { meetsApplianceDeliveryThreshold } from '../../pod/helpers/pod-fulfillment-utils';
import { getDeliveryTimeline } from '../helper/timeZoneUtils';

const getNotAvailableModel = () => ({
  method: FULFILLMENT_METHODS.APPLIANCE,
  enabled: false,
  template: <NotAvailable type={FULFILLMENT_METHODS.APPLIANCE} />
});

// FFA owns the ETA. The product query's cached date only stands in for a transient FFA miss,
// never for a state where delivery is not actually being offered.
const canUseCachedDeliveryDate = (applianceDeliveryData) => (
  !isApplianceDeliveryBackordered(applianceDeliveryData)
  && !applianceDeliveryData?.isDiscontinued
);

const getDeliveryStartDate = ({ applianceDeliveryData, cachedEarliestDeliveryDate }) => {
  const ffaEarliestDate = applianceDeliveryData?.earliestAvailabilityDate;
  if (ffaEarliestDate) {
    return { deliveryStartDate: ffaEarliestDate, isCachedDate: false };
  }
  if (cachedEarliestDeliveryDate && canUseCachedDeliveryDate(applianceDeliveryData)) {
    return { deliveryStartDate: cachedEarliestDeliveryDate, isCachedDate: true };
  }
  return { deliveryStartDate: null, isCachedDate: false };
};

const getFormattedEarliestDelivery = ({ deliveryStartDate, isCachedDate, isCartTiles }) => {
  if (!deliveryStartDate) {
    return null;
  }

  // FFA returns a yearless date that getApplianceDeliveryDate stamps with the right year, but the
  // cached product date is a full ISO date that already carries its own year.
  const skipYearCorrection = isCartTiles || isCachedDate;
  const correctedDeliveryStartDate = skipYearCorrection
    ? deliveryStartDate
    : getApplianceDeliveryDate({ earliestAvailabilityDate: deliveryStartDate }) ?? deliveryStartDate;

  return showTodayOrTomorrow(
    correctedDeliveryStartDate,
    false,
    getDeliveryTimeline(deliveryStartDate),
    formatDate(deliveryStartDate)
  );
};

const getHasFreeShipping = ({
  applianceDeliveryData,
  applianceDeliveryThreshold,
  enableApplianceDeliveryCharge,
  enableFreeDeliveryForExchange,
  isCartTiles,
  isExchangeCustomer,
  pricing
}) => {
  // FEATURE SWITCH OVERRIDE
  if (enableApplianceDeliveryCharge && !isExchangeCustomer) {
    return false;
  }
  // FEATURE SWITCH OVERRIDE
  if (isExchangeCustomer && enableFreeDeliveryForExchange) {
    return true;
  }
  if (isCartTiles) {
    return !!applianceDeliveryData?.hasFreeShipping;
  }
  return meetsApplianceDeliveryThreshold(pricing, applianceDeliveryThreshold);
};

const getApplianceFailureModel = ({
  applianceShippingTemplate,
  checkEarliestDateCta,
  formattedEarliestDelivery,
  isTransientFailure
}) => {
  if (isTransientFailure && formattedEarliestDelivery) {
    return {
      method: FULFILLMENT_METHODS.APPLIANCE,
      enabled: true,
      template: applianceShippingTemplate,
      subTemplate: <ApplianceShippingDetails />
    };
  }
  if (isTransientFailure && checkEarliestDateCta) {
    return {
      method: FULFILLMENT_METHODS.APPLIANCE,
      enabled: false,
      template: applianceShippingTemplate
    };
  }
  return getNotAvailableModel();
};

export const getApplianceDeliveryTemplate = ({
  applianceDeliveryData,
  cachedEarliestDeliveryDate = null,
  checkEarliestDateCta = null,
  pricing,
  enableApplianceDeliveryCharge,
  enableFreeDeliveryForExchange,
  applianceDeliveryThreshold,
  isExchangeCustomer,
  isCartTiles,
  hideShippingThreshold
}) => {
  if (!applianceDeliveryData) {
    return getNotAvailableModel();
  }

  const { deliveryStartDate, isCachedDate } = getDeliveryStartDate({
    applianceDeliveryData,
    cachedEarliestDeliveryDate
  });
  const formattedEarliestDelivery = getFormattedEarliestDelivery({
    deliveryStartDate,
    isCachedDate,
    isCartTiles
  });

  const applianceShippingTemplate = (
    <ApplianceShipping
      formattedEarliestDelivery={formattedEarliestDelivery}
      hasFreeShipping={getHasFreeShipping({
        applianceDeliveryData,
        applianceDeliveryThreshold,
        enableApplianceDeliveryCharge,
        enableFreeDeliveryForExchange,
        isCartTiles,
        isExchangeCustomer,
        pricing
      })}
      hideShippingThreshold={hideShippingThreshold}
      checkEarliestDateCta={checkEarliestDateCta}
    />
  );

  const isApplianceUnavailable = isApplianceDeliveryUnavailable(applianceDeliveryData);
  if (isApplianceUnavailable || isApplianceDeliveryError(applianceDeliveryData)) {
    return getApplianceFailureModel({
      applianceShippingTemplate,
      checkEarliestDateCta,
      formattedEarliestDelivery,
      isTransientFailure: !isApplianceUnavailable
    });
  }

  if (isApplianceDeliveryOutOfStock(applianceDeliveryData)) {
    return {
      method: FULFILLMENT_METHODS.APPLIANCE,
      enabled: false,
      unavailable: true, // we want to show the OOS msg and not the email notifyme
      template: <NotAvailable type={FULFILLMENT_METHODS.APPLIANCE} />
    };
  }

  return {
    method: FULFILLMENT_METHODS.APPLIANCE,
    enabled: true,
    template: applianceShippingTemplate,
    subTemplate: <ApplianceShippingDetails />
  };
};
